import React, { useState, useEffect, useMemo } from 'react';
import { formatAppDate } from '../services/appConfigRuntime';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      // Try to get username from localStorage first, fallback to extracting from token
      let username = localStorage.getItem('username');
      
      if (!username) {
        // Try to extract username from JWT token
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          username = tokenPayload.sub || tokenPayload.username || tokenPayload.user_name || tokenPayload.preferred_username;
          console.log('Extracted username from token:', username);
          
          // Store the extracted username in localStorage for future use
          if (username) {
            localStorage.setItem('username', username);
          }
        } catch (tokenError) {
          console.error('Could not extract username from token:', tokenError);
        }
      }

      if (!username) {
        throw new Error('Unable to determine username. Please login again.');
      }

      const url = `${process.env.REACT_APP_BACKEND_URL}synctrack/profile/${username}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        setProfile(data);
    } catch (err) {
      console.error('❌ Profile: Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return formatAppDate(dateString);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    if (typeof status === 'string') {
      switch (status.toLowerCase()) {
        case 'active':
          return 'success';
        case 'inactive':
          return 'error';
        default:
          return 'default';
      }
    }
    return status === 1 ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading profile: {error}
        </Alert>
        <Button variant="contained" onClick={fetchProfile}>
          Retry
        </Button>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          No profile data available
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      {/* Profile Header */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          borderRadius: 3,
          backgroundColor: '#6B0F2A',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            transform: 'translate(50px, -50px)'
          }}
        />
        <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                fontSize: '2rem',
                fontWeight: 'bold',
                border: '3px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
              }}
            >
              {profile.firstName?.charAt(0)?.toUpperCase() || profile.username?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {profile.firstName} {profile.lastName}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1, opacity: 0.9 }}>
                {profile.email}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
                @{profile.username}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                Contact Information
              </Typography>
              
              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, ml: 3 }}>
                    {profile.email || 'Not provided'}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, ml: 3 }}>
                    {profile.phone || 'Not provided'}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Address
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, ml: 3 }}>
                    {profile.address || 'Not provided'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Company Information */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                Company Information
              </Typography>
              
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Company Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {profile.tenantName || 'Not provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tenant ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {profile.tenantId || 'Not provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Company Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {profile.tenantEmail || 'Not provided'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Country
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {profile.tenantCountry || 'Not provided'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Overview */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
                <CreditCardIcon sx={{ mr: 1, color: 'primary.main' }} />
                Subscription Overview
              </Typography>
              
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Plan Type
                  </Typography>
                  <Chip
                    label={profile.planType || 'Not set'}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={profile.planStatus || 'Not set'}
                    color={getStatusColor(profile.planStatus)}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
                {/* DEBUG: End of Subscription Overview Stack */}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Details */}
        <Grid item xs={12}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
                <CalendarIcon sx={{ mr: 1, color: 'info.main' }} />
                Subscription Details
              </Typography>
              
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: '#6B0F2A',
                    color: '#FFFFFF'
                  }}>
                    <CalendarIcon sx={{ fontSize: 40, mb: 1.5 }} />
                    <Typography variant="body2" gutterBottom sx={{ opacity: 0.9 }}>
                      Plan Start Date
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatDate(profile.planStartDate)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: '#6B0F2A',
                    color: '#FFFFFF'
                  }}>
                    <CalendarIcon sx={{ fontSize: 40, mb: 1.5 }} />
                    <Typography variant="body2" gutterBottom sx={{ opacity: 0.9 }}>
                      Plan Expiry Date
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatDate(profile.planEndDate)}
                    </Typography>
                  </Box>
                </Grid>

                {profile.userCount && profile.userCount > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: '#6B0F2A',
                      color: '#FFFFFF'
                    }}>
                      <PeopleIcon sx={{ fontSize: 40, mb: 1.5 }} />
                      <Typography variant="body2" gutterBottom sx={{ opacity: 0.9 }}>
                        Users Allowed
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {profile.userCount}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile;